"use client"

import { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ArrowLeft, ExternalLink, Play, Pause, ChevronLeft, ChevronRight } from "lucide-react"
import { useRouter, usePathname } from "next/navigation"
import { useTranslations } from "next-intl"
import { useLanguage } from "@/app/contexts/language-context"

export default function ECommercePlatformPage() {
  const router = useRouter();
  const pathname = usePathname();
  const isTransactionalRoute = pathname.includes('/transactional-infrastructure');
  const uiTokens = useTranslations();
  const { t, language } = useLanguage();
  const shaderCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [coords, setCoords] = useState({ x: "0.00", y: "0.00", z: "0.00" });
  // HUD Layer state (hash-based)
  const [activeLayer, setActiveLayer] = useState('MAIN-HUD');
  // Hash-based HUD layer switching
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '') || 'main-hud';
      setActiveLayer(hash.toUpperCase());
    };
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const latchTransition = {
    type: "spring" as const,
    stiffness: 400,
    damping: 30,
    restDelta: 0.001,
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isTransactionalRoute || !shaderCanvasRef.current) return;

    let renderer: any;
    let frameId = 0;
    let disposeResize: (() => void) | null = null;

    const init = async () => {
      const THREE = await import('three');
      if (!shaderCanvasRef.current) return;

      const canvas = shaderCanvasRef.current;
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x0f172a);

      const camera = new THREE.PerspectiveCamera(45, canvas.clientWidth / canvas.clientHeight, 0.1, 100);
      camera.position.set(0, 0.2, 4.2);

      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);

      const keyLight = new THREE.DirectionalLight(0xffffff, 2.1);
      keyLight.position.set(3, 4, 3);
      scene.add(keyLight);
      scene.add(new THREE.AmbientLight(0x9ca3af, 1.2));
      const rimLight = new THREE.DirectionalLight(0x9ad9ff, 1.5);
      rimLight.position.set(-2, 1, -3);
      scene.add(rimLight);

      const geometry = new THREE.TorusKnotGeometry(1.0, 0.35, 220, 32);
      const material = new THREE.MeshStandardMaterial({
        color: 0xb7bec7,
        metalness: 1,
        envMapIntensity: 2.8,
        roughness: 0.12,
      });
      const mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);

      const clock = new THREE.Clock();
      const animate = () => {
        const t = clock.getElapsedTime();
        mesh.rotation.y = t * 0.45;
        mesh.rotation.x = Math.sin(t * 0.25) * 0.18;
        renderer.render(scene, camera);
        frameId = window.requestAnimationFrame(animate);
      };
      animate();

      const onResize = () => {
        if (!shaderCanvasRef.current || !renderer) return;
        const w = shaderCanvasRef.current.clientWidth;
        const h = shaderCanvasRef.current.clientHeight;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h, false);
      };
      window.addEventListener('resize', onResize);
      disposeResize = () => window.removeEventListener('resize', onResize);
    };

    init();

    return () => {
      window.cancelAnimationFrame(frameId);
      disposeResize?.();
      renderer?.dispose?.();
    };
  }, [isTransactionalRoute]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCoords({
        x: (Math.random() * 10).toFixed(2),
        y: (Math.random() * 10).toFixed(2),
        z: (Math.random() * 10).toFixed(2),
      });
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const projectImages = [
    {
      // Homepage Design
      src: "/projects/ecommerce/screenshots/ecommerce-homepage-1440x900.jpg",
      alt: "Homepage Design",
      title: t('projects.e-commerce-platform.gallery.0.title') || "Homepage Design",
      description: t('projects.e-commerce-platform.gallery.0.description') || "Modern and responsive homepage with featured products and categories",
    },
    {
      // Product detail page (tablet)
      src: "/projects/ecommerce/screenshots/ecommerce-product-detail-1024x768.jpg",
      alt: "Product detail page (tablet)",
      title: t('projects.e-commerce-platform.gallery.1.title') || "Product Detail Page",
      description: t('projects.e-commerce-platform.gallery.1.description') || "Detailed product view with image gallery, reviews, and purchase options",
    },
    {
      // Mobile product listing
      src: "/projects/ecommerce/screenshots/ecommerce-mobile-listing-390x844.jpg",
      alt: "Mobile product listing",
      title: t('projects.e-commerce-platform.gallery.2.title') || "Shopping Cart",
      description: t('projects.e-commerce-platform.gallery.2.description') || "Intuitive shopping cart with quantity controls and price calculations",
    },
    {
      // Demo video: checkout flow
      src: "/projects/ecommerce/screenshots/ecommerce-desktop-1440x900.jpg",
      alt: "Demo video: checkout flow",
      title: t('projects.e-commerce-platform.gallery.3.title') || "Checkout Process",
      description: t('projects.e-commerce-platform.gallery.3.description') || "Streamlined checkout with multiple payment options and order summary",
      videoSrc: "/projects/ecommerce/screenshots/ecommerce-demo-checkout.mp4",
    },
    {
      // Admin dashboard (placeholder)
      src: "/projects/ecommerce/screenshots/ecommerce-admin-dashboard-1440x900.jpg",
      alt: "Admin dashboard (placeholder)",
      title: t('projects.e-commerce-platform.gallery.4.title') || "Admin Dashboard",
      description: t('projects.e-commerce-platform.gallery.4.description') || "Comprehensive admin panel for inventory, orders, and analytics management",
    },
    {
      // Mobile app interface (placeholder)
      src: "/projects/ecommerce/screenshots/ecommerce-mobile-app-390x844.jpg",
      alt: "Mobile app interface (placeholder)",
      title: t('projects.e-commerce-platform.gallery.5.title') || "Mobile Application",
      description: t('projects.e-commerce-platform.gallery.5.description') || "Native mobile app with seamless shopping experience and push notifications",
    },
  ];

  const processSteps = [
    {
      step: "01",
      title: t('projects.e-commerce-platform.process.1.title') || "Repository Audit",
      description: t('projects.e-commerce-platform.process.1.description') || "Mid-flight integration into the active agency repository to map data flow, calculate performance budgets, and audit technical debt.",
      tools: ["GitHub", "Next.js App Router"],
    },
    {
      step: "02",
      title: t('projects.e-commerce-platform.process.2.title') || "Architecture Blueprinting",
      description: t('projects.e-commerce-platform.process.2.description') || "Generation of Technical Architecture Documents (TADs) to establish strict TypeScript schemas and API routing boundaries.",
      tools: ["TypeScript", "REST/GraphQL"],
    },
    {
      step: "03",
      title: t('projects.e-commerce-platform.process.3.title') || "Asset Decimation",
      description: t('projects.e-commerce-platform.process.3.description') || "Optimizing massive CAD/glTF files via Blender to radically reduce polygon counts and minimize GPU draw-calls.",
      tools: ["Blender", "glTF-Pipeline"],
    },
    {
      step: "04",
      title: t('projects.e-commerce-platform.process.4.title') || "Shader Engineering",
      description: t('projects.e-commerce-platform.process.4.description') || "Writing custom, lightweight GLSL shader logic to guarantee a flawless 60 FPS profile on mobile browser edge networks.",
      tools: ["GLSL", "React Three Fiber"],
    },
    {
      step: "05",
      title: t('projects.e-commerce-platform.process.5.title') || "State Synchronization",
      description: t('projects.e-commerce-platform.process.5.description') || "Binding the highly-optimized WebGL front-end to the client's manufacturing/inventory logic with zero-latency state management.",
      tools: ["Zustand", "PostgreSQL"],
    },
    {
      step: "06",
      title: t('projects.e-commerce-platform.process.6.title') || "Handshake & Deployment",
      description: t('projects.e-commerce-platform.process.6.description') || "Final code handover and CI/CD deployment, successfully clearing the agency's compressed launch deadline.",
      tools: ["Vercel Edge", "CI/CD Actions"],
    },
  ];

  const projectSpecs = {
    client: t('projects.e-commerce-platform.specs.client') || "Confidential Agency Partner",
    duration: t('projects.e-commerce-platform.specs.duration') || "3-Week Production Sprint",
    deliverables: t('projects.e-commerce-platform.specs.deliverables') || "WebGL Configurator",
    technology: t('projects.e-commerce-platform.specs.technology') || "Next.js + React Three Fiber",
    features: t('projects.e-commerce-platform.specs.features') || "Decimation, State Sync",
    deployment: t('projects.e-commerce-platform.specs.deployment') || "Vercel Edge",
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % projectImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + projectImages.length) % projectImages.length);
  };

  const toggleSlideshow = () => {
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    let slideshowInterval: NodeJS.Timeout | null = null;
    if (isPlaying) {
      slideshowInterval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % projectImages.length);
      }, 5000);
    }
    return () => {
      if (slideshowInterval) clearInterval(slideshowInterval);
    };
  }, [isPlaying, projectImages.length]);

  return (
    <>
      <main className="main-content pb-16">
        <div className="relative h-[70vh] min-h-[500px] w-full bg-gradient-to-br from-blue-900 to-purple-800 overflow-hidden">
          <div className="absolute inset-0 opacity-50">
            {isTransactionalRoute ? (
              <canvas ref={shaderCanvasRef} className="w-full h-full" aria-label="P03 Titanium Material Shader Canvas" />
            ) : (
              <img
                src={projectImages[currentImageIndex].src || "/placeholder.svg"}
                alt={projectImages[currentImageIndex].alt}
                className="w-full h-full object-cover"
              />
            )}
          </div>

          <svg className="absolute inset-0 w-full h-full pointer-events-none text-emerald-300/40">
            <line x1="32" y1="32" x2="164" y2="32" stroke="currentColor" strokeWidth="1" />
            <line x1="32" y1="32" x2="32" y2="128" stroke="currentColor" strokeWidth="1" />
            <circle cx="32" cy="32" r="3" fill="currentColor" />
            <line x1="calc(100% - 32)" y1="calc(100% - 32)" x2="calc(100% - 164)" y2="calc(100% - 32)" stroke="currentColor" strokeWidth="1" />
            <line x1="calc(100% - 32)" y1="calc(100% - 32)" x2="calc(100% - 32)" y2="calc(100% - 128)" stroke="currentColor" strokeWidth="1" />
            <circle cx="calc(100% - 32)" cy="calc(100% - 32)" r="3" fill="currentColor" />
          </svg>

          <div className="absolute top-8 left-8 z-20 pointer-events-none">
            <span className="text-[10px] font-mono text-sky-500 uppercase tracking-widest block mb-1">{uiTokens('hud.kineticFlux')}</span>
            <AnimatePresence mode="wait">
              <motion.div
                key={`${coords.x}-${coords.y}-${coords.z}`}
                initial={{ opacity: 0.5, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0.45, scale: 0.98 }}
                transition={latchTransition}
                className="text-xs font-mono text-emerald-400 border border-emerald-400/30 rounded px-2 py-1 bg-black/30"
              >
                X:{coords.x} Y:{coords.y} Z:{coords.z}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="absolute bottom-8 left-8 z-20 pointer-events-none">
            <div className="text-[10px] font-mono text-sky-500 uppercase tracking-wider mb-1">{uiTokens('hud.nodeId')}</div>
            <motion.div
              key={`${currentImageIndex}-${isPlaying ? 'run' : 'hold'}`}
              initial={{ y: 6, opacity: 0.6 }}
              animate={{ y: 0, opacity: 1 }}
              transition={latchTransition}
              className="text-xs font-mono text-sky-500 border border-sky-500/30 rounded px-2 py-1 bg-black/30"
            >
              {uiTokens('hud.architectureLabel')} ::{" "}
              {isPlaying ? (
                uiTokens('hud.networkStatus')
              ) : (
                <>
                  <span className="inline sm:hidden">{uiTokens('hud.latchProtocolMobile')}</span>
                  <span className="hidden sm:inline">{uiTokens('hud.latchProtocol')}</span>
                </>
              )}
            </motion.div>
          </div>

          <div className="absolute bottom-8 right-8 z-20 pointer-events-none text-right">
            <div className="text-[10px] font-mono text-sky-500 uppercase tracking-wider mb-1">{uiTokens('hud.securityStatus')}</div>
            <motion.div
              key={`${coords.x}-${coords.z}`}
              initial={{ x: 8, opacity: 0.6 }}
              animate={{ x: 0, opacity: 1 }}
              transition={latchTransition}
              className="text-xs font-mono text-sky-500 border border-sky-500/30 rounded px-2 py-1 bg-black/30"
            >
              latency: {" "}
              {(Math.abs(Number(coords.x) - Number(coords.z)) * 9.7).toFixed(1)}ms
            </motion.div>
          </div>

          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>

          <div className="absolute inset-0 flex items-center justify-between px-6 z-10">
            <Button variant="ghost" size="icon" className="text-white bg-black/20 hover:bg-black/40 backdrop-blur-sm" onClick={prevImage}>
              <ChevronLeft className="h-6 w-6" />
            </Button>
            <Button variant="ghost" size="icon" className="text-white bg-black/20 hover:bg-black/40 backdrop-blur-sm" onClick={nextImage}>
              <ChevronRight className="h-6 w-6" />
            </Button>
          </div>

          <div className="container mx-auto px-6 relative z-10 h-full flex flex-col justify-end pb-12">
            <Button variant="ghost" size="sm" className="text-white mb-6 w-fit hover:bg-white/20" onClick={() => router.back()}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('projects.detail.back')}
            </Button>

            <Badge className="mb-4 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 w-fit">
              {t('projects.e-commerce-platform.badge') || 'Full-Stack E-Commerce Development'}
            </Badge>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 max-w-4xl">{t('projects.e-commerce-platform.title') || 'Modern E-Commerce Platform'}</h1>

            <p className="text-xl text-white/80 mb-6 max-w-3xl">{t('projects.e-commerce-platform.description') || 'A comprehensive e-commerce solution built with Next.js and Django, featuring advanced payment processing, inventory management, and analytics dashboard'}</p>

            <div className="flex flex-wrap items-center gap-4 text-white/80 mb-6">
              <span>Client: {projectSpecs.client}</span>
              <span>•</span>
              <span>Duration: {projectSpecs.duration}</span>
              <span>•</span>
              <span>{projectSpecs.deliverables}</span>
            </div>

            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" className="text-white border-white/30 hover:bg-white/20 bg-transparent" onClick={toggleSlideshow}>
                {isPlaying ? <Pause className="mr-2 h-4 w-4" /> : <Play className="mr-2 h-4 w-4" />}
                {isPlaying ? t('projects.detail.pauseSlideshow') : t('projects.detail.playSlideshow')} {t('projects.detail.slideshow')}
              </Button>
              <Button
                size="sm"
                className="bg-transparent border border-white/10 hover:border-emerald-400 transition-colors text-emerald-400 font-semibold group relative px-6 py-2"
                onClick={() => {
                  const nextLayer = activeLayer === 'MAIN-HUD' ? 'data-stream' : 'main-hud';
                  router.push(`/${language}/projects/e-commerce-platform#${nextLayer}`);
                }}
              >
                <span className="text-emerald-400">
                  {uiTokens('navigation.shiftProtocol')} :: {uiTokens('navigation.telemetrySync')}
                </span>
              </Button>
              <div className="text-sm text-white/60">{currentImageIndex + 1} / {projectImages.length}</div>
            </div>
          </div>

          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
            {projectImages.map((_, index) => (
              <button key={index} aria-label={`Go to image ${index + 1}`} className={`w-2 h-2 rounded-full transition-all ${index === currentImageIndex ? 'bg-white' : 'bg-white/40'}`} onClick={() => setCurrentImageIndex(index)} />
            ))}
          </div>
        </div>

        <div className="container mx-auto px-6 py-16">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-12">
              <TabsTrigger value="overview">{t('projects.detail.tabs.overview')}</TabsTrigger>
              <TabsTrigger value="gallery">{t('projects.detail.tabs.gallery')}</TabsTrigger>
              <TabsTrigger value="process">{t('projects.detail.tabs.process')}</TabsTrigger>
              <TabsTrigger value="specs">{t('projects.detail.tabs.specs')}</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-12">
              <div className="grid md:grid-cols-2 gap-12">
                <div>
                  <h2 className="text-3xl font-bold mb-6">{t('projects.detail.overview')}</h2>
                  <div className="prose prose-lg dark:prose-invert">
                    <p>{t('projects.e-commerce-platform.overview.p1')}</p>
                    <p>{t('projects.e-commerce-platform.overview.p2')}</p>
                    <p>{t('projects.e-commerce-platform.overview.p3')}</p>
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold mb-6">{t('projects.detail.keyFeatures')}</h3>
                  <div className="space-y-4">
                    {([1, 2, 3, 4] as number[]).map((num) => {
                      const title = t(`projects.e-commerce-platform.features.${num}.title`) || ''
                      const description = t(`projects.e-commerce-platform.features.${num}.description`) || ''
                      return (
                        <div key={num} className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                          <div>
                            <h4 className="font-semibold">{title}</h4>
                            <p className="text-medium-contrast">{description}</p>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="gallery" className="space-y-8">
              <h2 className="text-3xl font-bold text-center mb-12">{t('projects.detail.gallery')}</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projectImages.map((image, index) => (
                  <Card key={index} className="group overflow-hidden hover:shadow-xl transition-all duration-300">
                    <div className="relative overflow-hidden">
                      <img src={image.src || "/placeholder.svg"} alt={image.alt} className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-white bg-black/20 hover:bg-black/40 backdrop-blur-sm"
                          onClick={() => {
                            if (image.videoSrc) {
                              // open the captured demo video in a new tab
                              window.open(image.videoSrc, '_blank')
                            } else {
                              window.open('https://example-ecommerce-demo.com', '_blank')
                            }
                          }}
                        >
                          <ExternalLink className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-2">{image.title}</h3>
                      <p className="text-sm text-medium-contrast">{image.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="process" className="space-y-12">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">{t('projects.detail.process')}</h2>
                <p className="text-xl text-medium-contrast max-w-3xl mx-auto">{t('projects.e-commerce-platform.processIntro') || 'Comprehensive analysis of business requirements, target audience, and competitive landscape to define project scope.'}</p>
              </div>

              <div className="space-y-8">
                {processSteps.map((step, index) => (
                  <div key={index} className="flex gap-8 items-start">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">{step.step}</div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold mb-3">{step.title}</h3>
                      <p className="text-medium-contrast mb-4 text-lg">{step.description}</p>
                      <div className="flex flex-wrap gap-2">{step.tools.map((tool) => (<Badge key={tool} variant="outline" className="text-sm">{tool}</Badge>))}</div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="specs" className="space-y-8">
              <h2 className="text-3xl font-bold text-center mb-12">{t('projects.detail.specs')}</h2>

              <div className="grid md:grid-cols-2 gap-8">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-6">{t('projects.detail.projectDetails')}</h3>
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span className="text-medium-contrast">{t('projects.detail.specLabels.client')}</span>
                        <span className="font-medium">{projectSpecs.client}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-medium-contrast">{t('projects.detail.specLabels.duration')}</span>
                        <span className="font-medium">{projectSpecs.duration}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-medium-contrast">{t('projects.detail.specLabels.deliverables')}</span>
                        <span className="font-medium">{projectSpecs.deliverables}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-medium-contrast">{t('projects.detail.specLabels.technology')}</span>
                        <span className="font-medium">{projectSpecs.technology}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-medium-contrast">{t('projects.detail.specLabels.features')}</span>
                        <span className="font-medium">{projectSpecs.features}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-medium-contrast">{t('projects.detail.specLabels.deployment')}</span>
                        <span className="font-medium">{projectSpecs.deployment}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-6">{t('projects.detail.technologyStack') || 'Technology Stack'}</h3>
                    <div className="flex flex-wrap gap-2">
                      {['Next.js 15', 'TypeScript', 'React Three Fiber', 'GLSL', 'Zustand', 'Vercel Edge'].map((tech) => (
                        <Badge key={tech} variant="outline" className="text-sm">{tech}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>

      </main>
    </>
  );
}
